<template>
  <van-row class="page-wraper payment-page">
    <payment-loading-animation
      v-if="showPaymentAnimation"
      :result="paymentAnimationResult"
      @go-home="onAnimationGoHome"
      @open-donora="openDonoraFromPayment"
    />
    <loader :show="showLoader && !showPaymentAnimation" />
    <template v-if="paymentUrl">
      <van-col class="flex-display-fix" span="24">
        <div v-if="$store.state.Settings.debug" class="debug-info">
          <p>Тип платежа: {{ paymentType }}</p>
          <p>URL платежа: {{ paymentUrl }}</p>
          <p>URL Нафтогаза: {{ $store.state.Payment.paymentUrl }}</p>
          <p>URL Распределения: {{ distributionPaymentUrl }}</p>
        </div>
        <div class="iframe-container">
          <iframe
            :src="paymentUrl"
            allowpaymentrequest
            frameborder="0"
            class="payment-page"
            ref="payPage"
          ></iframe>
        </div>
      </van-col>
    </template>
    <template v-else-if="!showPaymentAnimation">
      <van-col span="24" class="unified-payment">

        <!-- Payment summary -->
        <div class="payment-summary" v-if="displayAmount > 0">
          <!-- Debt info -->
          <div class="summary-debt" v-if="totalDebt > 0">
            <span class="debt-label">{{ $t("paymentPage.totalDebt") }}:</span>
            <span class="debt-value">{{ parseFloat(totalDebt).toFixed(2) }} {{ $t("components.accounts.currency") }}</span>
          </div>

          <!-- Amount to pay -->
          <div class="summary-row">
            <span class="summary-label">{{ $t("paymentPage.amountToPay") }}:</span>
            <span class="summary-value">{{ parseFloat(displayAmount).toFixed(2) }} {{ $t("components.accounts.currency") }}</span>
          </div>

          <!-- Services breakdown -->
          <div class="summary-services">
            <div class="summary-service" v-if="naftogazDisplayAmount > 0">
              <span class="service-name">{{ $t("paymentPage.gasSupply") }}</span>
              <span class="service-amount">{{ parseFloat(naftogazDisplayAmount).toFixed(2) }} {{ $t("components.accounts.currency") }}</span>
            </div>
            <div class="summary-service" v-if="distributionDisplayAmount > 0">
              <span class="service-name">{{ $t("paymentPage.gasDistribution") }}</span>
              <span class="service-amount">{{ parseFloat(distributionDisplayAmount).toFixed(2) }} {{ $t("components.accounts.currency") }}</span>
            </div>
          </div>

          <!-- Account numbers -->
          <div class="summary-account" v-if="$route.params.account">
            <span>{{ $t("paymentPage.accountNumber") }} {{ $route.params.account }}</span>
          </div>
          <div class="summary-account" v-if="distributionLsDisplay">
            <span>{{ $t("paymentPage.distributionAccount") }} {{ distributionLsDisplay }}</span>
          </div>
        </div>

        <!-- Saved cards list -->
        <template v-if="cards.length">
          <p class="cards-title">{{ $t("paymentPage.chooseCard") }}</p>
          <div class="cards-list">
            <div
              v-for="card in cards"
              :key="card.pan"
              class="card-item"
              @click="chooseCard(card.card_token)"
            >
              <div class="card-logo">
                <img
                  v-if="card.pan.charAt(0) === '4'"
                  src="@/assets/images/visa.png"
                  class="card-logo-img"
                />
                <img
                  v-else
                  src="@/assets/images/mastercard.png"
                  class="card-logo-img"
                />
              </div>
              <span class="card-pan">{{ card.pan }}</span>
              <span class="card-pay-label">{{ $t("paymentPage.payWithCard") }} →</span>
            </div>
          </div>
        </template>

        <!-- Separator (only when there are saved cards) -->
        <p class="separator-text" v-if="cards.length">{{ $t("paymentPage.orEnterNewCard") }}</p>

        <!-- Inline card form -->
        <div class="card-form-container">
          <div class="form-group">
            <label>{{ $t("cardForm.cardNumber") }}</label>
            <custom-input
              v-model="cardData.cardNumber"
              type="text"
              :placeholder="$t('cardForm.cardNumberPlaceholder')"
              :error="errors.cardNumber"
              maxlength="19"
              @input="formatCardNumber"
              @blur="validateCardNumber"
            />
          </div>

          <div class="form-row">
            <div class="form-group half">
              <label>{{ $t("cardForm.expiryDate") }}</label>
              <custom-input
                v-model="cardData.expiry"
                type="text"
                :placeholder="$t('cardForm.expiryDatePlaceholder')"
                :error="errors.expiry"
                maxlength="5"
                @input="formatExpiry"
                @blur="validateExpiry"
              />
            </div>
            <div class="form-group half">
              <label>{{ $t("cardForm.cvv") }}</label>
              <custom-input
                v-model="cardData.cvv"
                type="text"
                :placeholder="$t('cardForm.cvvPlaceholder')"
                :error="errors.cvv"
                maxlength="3"
                @input="formatCvv"
                @blur="validateCvv"
              />
            </div>
          </div>

          <div v-if="cardError" class="card-error">{{ cardError }}</div>
          <div v-if="cardSuccess" class="success-message">{{ $t("cardForm.cardAddedSuccess") }}</div>

          <div class="terms-checkbox">
            <label class="terms-label">
              <input
                v-model="termsAccepted"
                type="checkbox"
                class="terms-input"
                @change="cardError = ''"
              />
              <span class="terms-text">
                {{ $t("NoComissionPay.termsAgreement") }}
                <router-link
                  :to="{ name: 'TermsPage' }"
                  class="terms-link"
                  @click.native.stop
                >
                  {{ $t("NoComissionPay.termsAgreementLink") }} </router-link
                >.
              </span>
            </label>
          </div>

          <!-- Pay button -->
          <button
            class="pay-btn"
            :disabled="cardLoading || !isFormValid"
            @click="handleNewCardPayment"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M13.5 0.67s0.74 2.65 0.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l0.03-0.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5 0.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-0.36 3.6-1.21 4.62-2.58 0.39 1.29 0.59 2.65 0.59 4.04 0 2.65-2.15 4.8-4.8 4.8z"/>
            </svg>
            {{ cardLoading ? $t('cardForm.saving') : payButtonText }}
          </button>

          <!-- Security badge -->
          <div class="security-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.4)">
              <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/>
            </svg>
            <span>{{ $t("paymentPage.securePayment") }}</span>
            <span class="badge-visa">Visa</span>
            <span class="badge-mc">Mastercard</span>
          </div>
        </div>

      </van-col>
    </template>
    <simple-dialog
      v-model="showDialog"
      :success="true"
      :title="this.$t('paymentPage.phoneTitle')"
    />
  </van-row>
</template>
<script>
import { loadStripe } from "@stripe/stripe-js";
import loader from "@/components/content/Loader";
import SimpleDialog from "@/components/content/SimpleDialog";
import PaymentLoadingAnimation from "@/components/payment/PaymentLoadingAnimation";
import CustomInput from "@/components/forms/CustomInput";
import { getDonoraUrl } from "@/views/account/edopomoga/donoraLinks";

const STRIPE_PUBLISHABLE_KEY =
  process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY ||
  "pk_live_51T6suzKAb7dXwnQeVFoVWy5I5Ei0kLLOTQov0c15rrJcb9ggs4tw0CteSDyHh4oBhHK5cBHDSaPoXLdhOdBXlsgm00DLahNh9x";
const STRIPE_3DS_CANCELED_MESSAGE =
  "Підтвердження в банку скасовано або не завершено. Спробуйте ще раз і підтвердіть дію в застосунку банку.";

export default {
  components: {
    loader,
    SimpleDialog,
    PaymentLoadingAnimation,
    CustomInput,
  },
  name: "PaymentPage",
  computed: {
    getPlatform() {
      return this.$store.state.Auth.platform;
    },
    getPhone() {
      return this.$store.state.Settings.settings.phone;
    },
    showLoader() {
      return this.$store.state.Api.loading;
    },
    paymentResponse() {
      return this.$store.state.Payment.paymentResponse;
    },
    paymentUrl() {
      const distributionModule = this.getDistributionModule();
      return (
        this.$store.state[distributionModule].paymentUrl ||
        this.$store.state.Payment.paymentUrl
      );
    },
    account() {
      return this.$store.state.Auth.account;
    },
    cards() {
      return this.$store.state.Payment.cards;
    },
    distributionPaymentResponse() {
      const distributionModule = this.getDistributionModule();
      return this.$store.state[distributionModule].paymentResponse;
    },
    distributionPaymentUrl() {
      const distributionModule = this.getDistributionModule();
      return this.$store.state[distributionModule].paymentUrl;
    },
    paymentType() {
      return this.$route.params.paymentType || "naftogaz";
    },
    isCombinedPayment() {
      return (
        this.$route.params.naftogazAmount > 0 &&
        this.$route.params.distributionAmount > 0
      );
    },
    displayAmount() {
      const naftogaz = parseFloat(this.$route.params.naftogazAmount) || 0;
      const distribution = parseFloat(this.$route.params.distributionAmount) || 0;
      return naftogaz + distribution;
    },
    naftogazDisplayAmount() {
      return parseFloat(this.$route.params.naftogazAmount) || 0;
    },
    distributionDisplayAmount() {
      return parseFloat(this.$route.params.distributionAmount) || 0;
    },
    distributionLsDisplay() {
      return this.$store.state.Auth.account?.lsDistrib || null;
    },
    naftogazDebt() {
      const balance = this.$store.state.Auth.account?.balance;
      return balance > 0 ? balance : 0;
    },
    distributionDebt() {
      const distributionModule = this.getDistributionModule();
      const debt = this.$store.state[distributionModule]?.distributionDebt;
      return debt > 0 ? debt : 0;
    },
    totalDebt() {
      return this.naftogazDebt + this.distributionDebt;
    },
    isFormValid() {
      return (
        this.termsAccepted &&
        this.cardData.cardNumber.replace(/\s/g, "").length >= 16 &&
        this.cardData.expiry.length === 5 &&
        this.cardData.cvv.length >= 3
      );
    },
    payButtonText() {
      if (this.displayAmount > 0) {
        return this.$t("paymentPage.payAmount") + " " + parseFloat(this.displayAmount).toFixed(2) + " " + this.$t("components.accounts.currency");
      }
      return this.$t("paymentPage.payAmount");
    },
  },
  data() {
    return {
      watchInterval: null,
      showDialog: false,
      currentDistributionModule: null,
      showPaymentAnimation: false,
      paymentAnimationResult: null,
      cardData: {
        cardNumber: "",
        expiry: "",
        cvv: "",
      },
      errors: {
        cardNumber: "",
        expiry: "",
        cvv: "",
      },
      cardLoading: false,
      cardSuccess: false,
      cardError: "",
      termsAccepted: true,
      stripe: null,
      stripePublishableKey: null,
    };
  },
  created() {
    this.$store.dispatch("Settings/getPhone");

    const paymentStep = localStorage.getItem("combinedPaymentStep");
    const isSecondStep = paymentStep === "second";

    if (isSecondStep) {
      console.log("Обнаружен второй шаг комбинированного платежа");
      const distributionUrl = localStorage.getItem("distributionUrl");
      if (distributionUrl) {
        this.paymentType = "distribution";
        const appId =
          localStorage.getItem("distributionAppId") ||
          this.$store.state.Auth.app_id;
        const distributionModule = this.getDistributionModule(appId);
        this.currentDistributionModule = distributionModule;
        this.$store.commit(`${distributionModule}/SET_PAYMENT_URL`, distributionUrl);
        localStorage.removeItem("combinedPaymentStep");
        localStorage.removeItem("naftogazUrl");
        localStorage.removeItem("distributionUrl");
        localStorage.removeItem("distributionAppId");
        setTimeout(() => {
          this.handlePaymentResponse();
        }, 500);
      } else {
        localStorage.removeItem("combinedPaymentStep");
      }
    } else {
      this.$store.commit("Payment/SET_PAYMENT_URL", null);
      const distributionModule = this.getDistributionModule();
      this.$store.commit(`${distributionModule}/SET_PAYMENT_URL`, null);
    }

    this.runCheck();

    localStorage.removeItem("autoPaymentCardToken");
    localStorage.removeItem("autoPaymentParams");
    localStorage.removeItem("fromPayment");
    localStorage.removeItem("paymentParams");

    this.loadCards();
  },
  destroyed() {
    clearInterval(this.watchInterval);
  },
  methods: {
    normalizeStripeResponse(raw) {
      if (!raw) return raw;
      if (raw.payment_state) return raw;
      if (raw.data && raw.data.payment_state) return raw.data;
      if (raw.data && raw.data.data && raw.data.data.payment_state) {
        return raw.data.data;
      }
      if (raw.response && raw.response.data) {
        return this.normalizeStripeResponse(raw.response.data);
      }
      return raw;
    },

    findStripe3dsPayload(raw, depth = 0) {
      if (!raw || depth > 4 || typeof raw !== "object") return null;

      const hasStripe3dsState = raw.payment_state === "stripe_3ds_required";
      const hasStripe3dsMessage =
        typeof raw.message === "string" &&
        raw.message.toLowerCase().includes("stripe 3ds");

      if (
        raw.client_secret &&
        (hasStripe3dsState || hasStripe3dsMessage || raw.intent_type)
      ) {
        return raw;
      }

      const nestedKeys = ["data", "response", "result", "payload", "error"];
      for (const key of nestedKeys) {
        const nested = this.findStripe3dsPayload(raw[key], depth + 1);
        if (nested) return nested;
      }

      return null;
    },

    isStripe3dsRequired(response) {
      return (
        response &&
        (response.payment_state === "stripe_3ds_required" ||
          this.findStripe3dsPayload(response))
      );
    },

    async getStripe(publishableKey = STRIPE_PUBLISHABLE_KEY) {
      if (!publishableKey) {
        throw new Error("Stripe publishable_key is missing.");
      }

      if (!this.stripe || this.stripePublishableKey !== publishableKey) {
        this.stripe = await loadStripe(publishableKey);
        this.stripePublishableKey = publishableKey;
      }

      if (!this.stripe) {
        throw new Error("Stripe не вдалося завантажити.");
      }

      return this.stripe;
    },

    async handleStripe3ds(data) {
      if (!data.client_secret) {
        throw new Error("Stripe 3DS client_secret is missing.");
      }

      const clientSecret = String(data.client_secret);
      const publishableKey = data.publishable_key;
      if (!publishableKey) {
        throw new Error("Stripe publishable_key is missing.");
      }
      const stripe = await this.getStripe(publishableKey);
      const isSetupIntent =
        data.intent_type === "setup" || clientSecret.startsWith("seti_");
      const result =
        isSetupIntent
          ? await stripe.confirmCardSetup(clientSecret)
          : await stripe.confirmCardPayment(clientSecret);

      if (result.error) {
        console.log("3DS error", {
          code: result.error.code,
          message: result.error.message,
          clientSecretPrefix: clientSecret.slice(0, 12),
          keyPrefix: publishableKey.slice(0, 16),
          intentType: data.intent_type,
        });
        throw new Error(STRIPE_3DS_CANCELED_MESSAGE);
      }

      return result;
    },

    async runStripe3dsIfRequired(response) {
      const normalized = this.normalizeStripeResponse(response);
      const stripe3dsPayload = this.findStripe3dsPayload(response) || normalized;

      if (!this.isStripe3dsRequired(stripe3dsPayload)) {
        return false;
      }

      await this.handleStripe3ds(stripe3dsPayload);
      return true;
    },

    extractSavedCardId(raw, depth = 0) {
      if (!raw || depth > 4 || typeof raw !== "object") return null;

      const directKeys = ["card_id", "saved_card_id", "savedCardId", "cardId"];
      for (const key of directKeys) {
        if (raw[key] !== undefined && raw[key] !== null) {
          return String(raw[key]);
        }
      }

      if (
        raw.id !== undefined &&
        raw.id !== null &&
        (raw.masked || raw.pan || raw.brand || raw.last4)
      ) {
        return String(raw.id);
      }

      const nestedKeys = ["card", "saved_card", "savedCard", "data", "result", "payload"];
      for (const key of nestedKeys) {
        const nested = this.extractSavedCardId(raw[key], depth + 1);
        if (nested) return nested;
      }

      return null;
    },

    resolveSavedCardToken(saveResponse, cards, cardNumber) {
      const savedCardId = this.extractSavedCardId(saveResponse);
      if (savedCardId) {
        return savedCardId;
      }

      const last4 = cardNumber.slice(-4);
      const sameLast4 = cards.filter((card) => {
        const pan = String(card.pan || card.masked || card.card_number || "");
        return pan.replace(/\D/g, "").endsWith(last4);
      });

      if (sameLast4.length) {
        const sorted = [...sameLast4].sort((a, b) => {
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });
        return String(sorted[0].card_token || sorted[0].id);
      }

      const sortedCards = [...cards].sort((a, b) => {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      const fallbackCard = sortedCards[0] || cards[cards.length - 1];
      return fallbackCard ? String(fallbackCard.card_token || fallbackCard.id) : null;
    },

    async loadCards() {
      await this.$store.commit("Payment/SET_CARD_TOKEN", null);
      await this.$store.dispatch("Payment/getCardsList");
      if (!this.cards.length) {
        // No cards -- user will use the inline form; no auto-redirect needed
      }
    },

    // --- Navigation helpers ---
    async safeNavigate(routeName) {
      try {
        const params = routeName === "Account"
          ? { name: routeName, params: { account: this.$route.params.account } }
          : { name: routeName };
        await this.$router.push(params);
      } catch (error) {
        if (error.name !== "NavigationDuplicated") {
          throw error;
        }
      }
    },

    async onAnimationGoHome() {
      this.showPaymentAnimation = false;
      this.paymentAnimationResult = null;
      await this.safeNavigate("Account");
    },
    openDonoraFromPayment() {
      this.openExternalUrl(getDonoraUrl(this.$store.state.Auth.app_id));
    },
    openExternalUrl(url) {
      if (typeof this.nativeSendEvent === "function") {
        this.nativeSendEvent("url", url);
        return;
      }

      window.open(url, "_blank", "noopener");
    },

    // --- Distribution module resolver ---
    getDistributionModule(appId) {
      if (this.currentDistributionModule) {
        return this.currentDistributionModule;
      }
      const currentAppId = appId || this.$store.state.Auth.app_id;
      const map = {
        "kharkiv.gas": "PaymentDistributionHarkiv",
        "cherkasy.gas": "PaymentDistributionCherkasy",
        "chernihiv.gas": "PaymentDistributionChernihiv",
        "chernivtsi.gas": "PaymentDistributionChernivtsi",
        "dnipro.gas": "PaymentDistributionDnipro",
        "khmelnytskyi.gas": "PaymentDistributionKhmelnytskyi",
        "kirovograd.gas": "PaymentDistributionKirovograd",
        "kyiv.gas": "PaymentDistributionKyiv",
        "lviv.gas": "PaymentDistributionLviv",
        "mykolaiv.gas": "PaymentDistributionMykolaiv",
        "prykarpattya.gas": "PaymentDistributionPrykarpattya",
        "rivne.gas": "PaymentDistributionRivne",
        "sumy.gas": "PaymentDistributionSumy",
        "ternopil.gas": "PaymentDistributionTernopil",
        "vinnytsia.gas": "PaymentDistributionVinnytsia",
        "volyn.gas": "PaymentDistributionVolyn",
        "zakarpattya.gas": "PaymentDistributionZakarpattya",
      };
      return map[currentAppId] || "PaymentDistributionHarkiv";
    },

    // --- Native event ---
    sendNativeEvent(eventName, eventData) {
      try {
        const nativeData = this.$store.state.Auth.nativeData || {};
        const platform = nativeData.platform;
        if (eventName === "url" && (!platform || platform === "web")) {
          window.open(eventData, "_blank");
          return;
        }
        if (platform === "android" && window.Android && typeof window.Android[eventName] === "function") {
          window.Android[eventName](eventData);
        } else if (platform === "ios" && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[eventName] && typeof window.webkit.messageHandlers[eventName].postMessage === "function") {
          window.webkit.messageHandlers[eventName].postMessage(eventData);
        }
      } catch (error) {
        if (eventName === "url" && typeof eventData === "string" && eventData.startsWith("http")) {
          window.open(eventData, "_blank");
        }
      }
    },

    // --- Choose existing saved card -> pay immediately ---
    async chooseCard(cardToken) {
      if (!this.termsAccepted) {
        this.cardError = this.$t("NoComissionPay.errorTermsNotAccepted");
        return;
      }

      this.showPaymentAnimation = true;
      this.paymentAnimationResult = null;

      this.cards.forEach((card) => { card.selected = false; });
      const selectedCard = this.cards.find(
        (card) => card.card_token === cardToken || card.token === cardToken
      );
      if (selectedCard) {
        selectedCard.selected = true;
      }

      await this.$store.commit("Payment/SET_CARD_TOKEN", cardToken);

      if (!this.currentDistributionModule) {
        this.currentDistributionModule = this.getDistributionModule();
      }
      await this.$store.commit(`${this.currentDistributionModule}/SET_CARD_TOKEN`, cardToken);

      await this.preparePaymentPage();
    },

    // --- New card form: save + pay ---
    formatCardNumber() {
      let value = this.cardData.cardNumber.replace(/\s/g, "").replace(/\D/g, "").substring(0, 16);
      const parts = [];
      for (let i = 0; i < value.length; i += 4) {
        parts.push(value.substring(i, i + 4));
      }
      this.cardData.cardNumber = parts.join(" ");
      if (this.errors.cardNumber) this.errors.cardNumber = "";
    },
    validateCardNumber() {
      const num = this.cardData.cardNumber.replace(/\s/g, "");
      if (!num) { this.errors.cardNumber = this.$t("cardForm.errors.cardNumberRequired"); return false; }
      if (num.length < 16) { this.errors.cardNumber = this.$t("cardForm.errors.cardNumberInvalid"); return false; }
      if (!this.luhnCheck(num)) { this.errors.cardNumber = this.$t("cardForm.errors.cardNumberInvalid"); return false; }
      this.errors.cardNumber = "";
      return true;
    },
    luhnCheck(cardNumber) {
      let sum = 0, isEven = false;
      for (let i = cardNumber.length - 1; i >= 0; i--) {
        let digit = parseInt(cardNumber.charAt(i), 10);
        if (isEven) { digit *= 2; if (digit > 9) digit -= 9; }
        sum += digit;
        isEven = !isEven;
      }
      return sum % 10 === 0;
    },
    formatExpiry() {
      let value = this.cardData.expiry.replace(/\D/g, "");
      if (value.length >= 2) value = value.substring(0, 2) + "/" + value.substring(2, 4);
      this.cardData.expiry = value.substring(0, 5);
      if (this.errors.expiry) this.errors.expiry = "";
    },
    validateExpiry() {
      const expiry = this.cardData.expiry;
      if (!expiry) { this.errors.expiry = this.$t("cardForm.errors.expiryRequired"); return false; }
      if (expiry.length !== 5) { this.errors.expiry = this.$t("cardForm.errors.expiryInvalid"); return false; }
      const monthNum = parseInt(expiry.split("/")[0], 10);
      if (monthNum < 1 || monthNum > 12) { this.errors.expiry = this.$t("cardForm.errors.expiryInvalid"); return false; }
      this.errors.expiry = "";
      return true;
    },
    formatCvv() {
      this.cardData.cvv = this.cardData.cvv.replace(/\D/g, "").substring(0, 3);
      if (this.errors.cvv) this.errors.cvv = "";
    },
    validateCvv() {
      const cvv = this.cardData.cvv;
      if (!cvv) { this.errors.cvv = this.$t("cardForm.errors.cvvRequired"); return false; }
      if (cvv.length < 3) { this.errors.cvv = this.$t("cardForm.errors.cvvInvalid"); return false; }
      this.errors.cvv = "";
      return true;
    },

    async handleNewCardPayment() {
      if (!this.termsAccepted) {
        this.cardError = this.$t("NoComissionPay.errorTermsNotAccepted");
        return;
      }

      if (!this.validateCardNumber() || !this.validateExpiry() || !this.validateCvv()) return;

      this.showPaymentAnimation = true;
      this.paymentAnimationResult = null;
      this.cardError = "";
      this.cardSuccess = false;

      try {
        await this.$store.commit("Payment/SET_CARD_TOKEN", null);
        if (!this.currentDistributionModule) {
          this.currentDistributionModule = this.getDistributionModule();
        }
        await this.$store.commit(`${this.currentDistributionModule}/SET_CARD_TOKEN`, null);

        const cleanCardNumber = this.cardData.cardNumber.replace(/\s/g, "");
        const saveCardPayload = {
          user_id: this.$store.state.Auth.user_id,
          card_number: cleanCardNumber,
          card_expiry: this.cardData.expiry,
          card_cvv: this.cardData.cvv,
        };
        let response = await this.$store.dispatch(
          "Payment/saveCardWithFullData",
          saveCardPayload
        );

        if (await this.runStripe3dsIfRequired(response)) {
          response = await this.$store.dispatch(
            "Payment/saveCardWithFullData",
            saveCardPayload
          );
        }

        if (response && response.success) {
          await this.$store.dispatch("Payment/getCardsList");

          const cards = this.$store.state.Payment.cards;
          if (!cards.length) {
            this.paymentAnimationResult = { success: false, message: this.$t("cardForm.errors.saveFailed") };
            return;
          }

          const cardToken = this.resolveSavedCardToken(
            response,
            cards,
            cleanCardNumber
          );

          if (!cardToken) {
            this.paymentAnimationResult = { success: false, message: this.$t("cardForm.errors.saveFailed") };
            return;
          }

          await this.$store.commit("Payment/SET_CARD_TOKEN", cardToken);
          await this.$store.commit(`${this.currentDistributionModule}/SET_CARD_TOKEN`, cardToken);

          await this.preparePaymentPage();
        } else {
          this.paymentAnimationResult = { success: false, message: response?.error || this.$t("cardForm.errors.saveFailed") };
        }
      } catch (e) {
        this.paymentAnimationResult = { success: false, message: e.message || this.$t("cardForm.errors.saveFailed") };
      }
    },

    // --- Payment flow (kept from original) ---
    runCheck() {
      this.watchInterval = setInterval(() => {
        if (this.$refs.payPage !== undefined) {
          try {
            let url = this.$refs.payPage.contentWindow.location.href;
            if (url.match(process.env.VUE_APP_PRODUCTION_URL) !== null) {
              clearInterval(this.watchInterval);
              this.$router.push(url.replace(process.env.VUE_APP_PRODUCTION_URL, ""));
            }
          } catch (e) { /* cross-origin */ }
        }
      }, 200);
    },

    async preparePaymentPage() {
      if (this.account === null) {
        await this.$store.dispatch("Auth/getUserAccount", this.$route.params.account);
      }
      await this.runPaymentRequest();
    },

    async runPaymentRequest() {
      const paymentType = this.paymentType;

      if (this.$route.params.naftogazAmount) {
        await this.$store.commit("Payment/SET_NAFTOGAZ_AMOUNT", this.$route.params.naftogazAmount);
      }
      if (this.$route.params.distributionAmount) {
        await this.$store.commit("Payment/SET_DISTRIBUTION_AMOUNT", this.$route.params.distributionAmount);
      }

      const paymentStep = localStorage.getItem("combinedPaymentStep");
      const isSecondStep = paymentStep === "second";

      if (isSecondStep) {
        const distributionUrl = localStorage.getItem("distributionUrl");
        if (distributionUrl) {
          this.paymentType = "distribution";
          const appId = localStorage.getItem("distributionAppId") || this.$store.state.Auth.app_id;
          const distributionModule = this.getDistributionModule(appId);
          this.currentDistributionModule = distributionModule;
          this.$store.commit(`${distributionModule}/SET_PAYMENT_URL`, distributionUrl);
          const fakeResponse = { status: "Need3Ds" };
          this.$store.commit(`${distributionModule}/SET_PAYMENT_RESPONSE`, fakeResponse);
          localStorage.removeItem("combinedPaymentStep");
          localStorage.removeItem("naftogazUrl");
          localStorage.removeItem("distributionUrl");
          localStorage.removeItem("distributionAppId");
          setTimeout(() => { this.handlePaymentResponse(); }, 500);
          return;
        } else {
          localStorage.removeItem("combinedPaymentStep");
        }
      }

      try {
        if (paymentType === "distribution") {
          await this.processDistributionPayment();
        } else if (paymentType === "naftogaz") {
          await this.processNaftogazPayment();
        } else if (this.isCombinedPayment) {
          await this.processCombinedPayment();
        } else {
          await this.processNaftogazPayment();
        }
        await this.handlePaymentResponse();
      } catch (e) {
        console.error("Payment error:", e);
        if (this.showPaymentAnimation) {
          this.paymentAnimationResult = {
            success: false,
            message: e?.message || STRIPE_3DS_CANCELED_MESSAGE,
          };
          return;
        }
        await this.safeNavigate("CancelPayment");
      }
    },

    async processNaftogazPayment() {
      await this.$store.commit("Payment/SET_USER_LS", this.$route.params.account);
      await this.$store.commit("Payment/SET_NAME_APP_ID",
        this.$store.state.Auth.nativeData?.id || this.$store.state.Auth.app_id
      );
      const naftogazAmount = this.$store.state.Payment.naftogazAmount;
      await this.$store.commit("Payment/UPDATE_AMOUNT", naftogazAmount);
      await this.$store.commit("Payment/SET_NAFTOGAZ_AMOUNT", naftogazAmount);
      this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);

      this.showPaymentAnimation = true;
      this.paymentAnimationResult = null;

      await this.$store.dispatch("Payment/getPaymentPage");
      let paymentResponse = this.$store.state.Payment.paymentResponse;

      if (await this.runStripe3dsIfRequired(paymentResponse)) {
        this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
        await this.$store.dispatch("Payment/getPaymentPage");
        paymentResponse = this.$store.state.Payment.paymentResponse;
      }

      if (paymentResponse && (paymentResponse.mode === 1 || paymentResponse.status === "success" || paymentResponse.success === true)) {
        this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
        this.paymentAnimationResult = { success: true, message: paymentResponse.message || "Платіж успішно оброблено" };
        return;
      } else if (paymentResponse && (paymentResponse.mode === 0 || paymentResponse.status === "error" || paymentResponse.success === false)) {
        this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
        this.paymentAnimationResult = { success: false, message: paymentResponse.error || paymentResponse.message || "Платіж відхилено банком" };
        return;
      }
      this.showPaymentAnimation = false;
    },

    async processDistributionPayment() {
      const distributionAmount = this.$store.state.Payment.distributionAmount;
      const distributionLs = this.$store.state.Auth.account?.lsDistrib;
      const city = this.$route.params.city;
      const appId = this.$store.state.Auth.app_id;
      const savedCardId = this.$store.state.Payment.paymentData.saved_card_id;

      if (!savedCardId) {
        console.error("Нет сохраненной карты для оплаты распределения");
        this.showPaymentAnimation = true;
        this.paymentAnimationResult = { success: false, message: "Спочатку збережіть картку для оплати" };
        return;
      }

      const serviceKey = this.getServiceKey(appId, city);
      console.log("Distribution payment with saved card:", { savedCardId, distributionLs, distributionAmount, serviceKey, city, appId });

      this.showPaymentAnimation = true;
      this.paymentAnimationResult = null;
      this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);

      const distributionPayload = {
        savedCardId: savedCardId,
        ls: distributionLs,
        amount: distributionAmount,
        serviceKey: serviceKey,
      };
      await this.$store.dispatch(
        "Payment/payDistributionWithSavedCard",
        distributionPayload
      );

      let distribResponse = this.$store.state.Payment.distributionSavedCardResponse;

      if (await this.runStripe3dsIfRequired(distribResponse)) {
        this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);
        await this.$store.dispatch(
          "Payment/payDistributionWithSavedCard",
          distributionPayload
        );
        distribResponse = this.$store.state.Payment.distributionSavedCardResponse;
      }

      if (distribResponse && (distribResponse.mode === 1 || distribResponse.success === true)) {
        this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);
        this.paymentAnimationResult = { success: true, message: distribResponse.message || "Платіж розподілу успішно оброблено" };
        return;
      } else if (distribResponse && (distribResponse.mode === 0 || distribResponse.success === false)) {
        this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);
        this.paymentAnimationResult = { success: false, message: distribResponse.error || distribResponse.message || "Платіж розподілу відхилено" };
        return;
      }
      this.showPaymentAnimation = false;
    },

    getServiceKey(appId, city) {
      const serviceKeyMap = {
        "lviv.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-LVIV",
        "kharkiv.gas": city === "Харків"
          ? "GAZOROZPODILNI-MEREZHI-UKRAYINI-KHARKIV"
          : "GAZMEREZHI-UKRAYINI-KHARKIV-OBLAST",
        "kyiv.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-KIYV",
        "dnipro.gas": city === "Дніпро"
          ? "GAZOROZPODILNI-MEREZHI-UKRAYINI-DNIPRO"
          : "GAZMEREZHI-DNIPROPETROVSKA-OBL",
        "vinnytsia.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-VINNYTSYA",
        "rivne.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-RIVNE",
        "sumy.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-SUMY",
        "ternopil.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-TERNOPIL-S",
        "khmelnytskyi.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-KHMELNYTSK",
        "kirovograd.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-KROP",
        "mykolaiv.gas": "GAZOROZPODILNI-MEREZHI-UKRAYINI-MYKOLAYIV",
        "prykarpattya.gas": "GAZOROZPODILNI-MEREZHI-IVANO-FRANKIVSK",
        "volyn.gas": "GAZOROZPODILNI-MEREZHI-VOLYN",
        "cherkasy.gas": "GAZOROZPODILNI-MEREZHI-CHERKASY",
        "chernihiv.gas": "GAZOROZPODILNI-MEREZHI-CHERNIGIV",
        "chernivtsi.gas": "GAZOROZPODILNI-MEREZHI-CHERNIVTSI",
        "zakarpattya.gas": "GAZOROZPODILNI-MEREZHI-ZAKARPATTYA",
      };
      return serviceKeyMap[appId] || null;
    },

    resolveDistributionAction(appId, city) {
      const actionMap = {
        "kharkiv.gas": city === "Харків" ? "createDistributionOrderHarkiv" : "createDistributionOrderHarkivRegion",
        "cherkasy.gas": "createDistributionOrderCherkasy",
        "chernihiv.gas": "createDistributionOrderChernihiv",
        "chernivtsi.gas": "createDistributionOrderChernivtsi",
        "dnipro.gas": city === "Дніпро" ? "createDistributionOrderDnipro" : "createDistributionOrderDniproRegion",
        "khmelnytskyi.gas": "createDistributionOrderKhmelnytskyi",
        "kirovograd.gas": "createDistributionOrderKirovograd",
        "kyiv.gas": "createDistributionOrderKyiv",
        "lviv.gas": "createDistributionOrderLviv",
        "mykolaiv.gas": "createDistributionOrderMykolaiv",
        "prykarpattya.gas": "createDistributionOrderPrykarpattya",
        "rivne.gas": "createDistributionOrderRivne",
        "sumy.gas": "createDistributionOrderSumy",
        "ternopil.gas": "createDistributionOrderTernopil",
        "vinnytsia.gas": "createDistributionOrderVinnytsia",
        "volyn.gas": "createDistributionOrderVolyn",
        "zakarpattya.gas": "createDistributionOrderZakarpattya",
      };
      return actionMap[appId];
    },

    async processCombinedPayment() {
      const savedCardId = this.$store.state.Payment.paymentData.saved_card_id;
      const appId = this.$store.state.Auth.app_id;
      const distributionLs = this.$store.state.Auth.account?.lsDistrib;
      const distributionAmount = this.$store.state.Payment.distributionAmount;
      const naftogazAmount = this.$store.state.Payment.naftogazAmount;
      const city = this.$route.params.city;

      if (!savedCardId) {
        console.error("Нет сохраненной карты для комбинированной оплаты");
        this.showPaymentAnimation = true;
        this.paymentAnimationResult = { success: false, message: "Спочатку збережіть картку для оплати" };
        return;
      }

      console.log("Combined payment with saved card — gas first, then distribution");
      this.showPaymentAnimation = true;
      this.paymentAnimationResult = null;

      await this.$store.commit("Payment/SET_USER_LS", this.$route.params.account);
      await this.$store.commit("Payment/UPDATE_AMOUNT", naftogazAmount);
      await this.$store.commit("Payment/SET_NAFTOGAZ_AMOUNT", naftogazAmount);
      this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
      this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);

      const serviceKey = this.getServiceKey(appId, city);

      // 1. Сначала оплата газа
      await this.$store.dispatch("Payment/getPaymentPage");
      let gasResponse = this.$store.state.Payment.paymentResponse;

      if (await this.runStripe3dsIfRequired(gasResponse)) {
        this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
        await this.$store.dispatch("Payment/getPaymentPage");
        gasResponse = this.$store.state.Payment.paymentResponse;
      }

      const gasSuccess = gasResponse && (gasResponse.mode === 1 || gasResponse.success === true);
      const gasFailed = gasResponse && (gasResponse.mode === 0 || gasResponse.success === false);

      if (gasFailed) {
        const gasError = gasResponse?.error || gasResponse?.message || "Платіж за газ відхилено";
        this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
        this.paymentAnimationResult = { success: false, message: gasError };
        return;
      }

      if (gasResponse && gasResponse.mode === 3) {
        this.showPaymentAnimation = false;
        return;
      }

      // 2. Потом оплата распределения
      const distributionPayload = {
        savedCardId: savedCardId,
        ls: distributionLs,
        amount: distributionAmount,
        serviceKey: serviceKey,
      };
      await this.$store.dispatch(
        "Payment/payDistributionWithSavedCard",
        distributionPayload
      );

      let distribResponse = this.$store.state.Payment.distributionSavedCardResponse;

      if (await this.runStripe3dsIfRequired(distribResponse)) {
        this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);
        await this.$store.dispatch(
          "Payment/payDistributionWithSavedCard",
          distributionPayload
        );
        distribResponse = this.$store.state.Payment.distributionSavedCardResponse;
      }

      const distribSuccess = distribResponse && (distribResponse.mode === 1 || distribResponse.success === true);
      const distribPartial = distribResponse && distribResponse.partial_success === true;
      const distribFailed = distribResponse && (distribResponse.mode === 0 || distribResponse.success === false);

      if (gasSuccess && distribSuccess) {
        this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
        this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);
        this.paymentAnimationResult = { success: true, message: "Обидва платежі успішно оброблено" };
        return;
      }

      if (gasSuccess && distribPartial) {
        this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
        this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);
        this.paymentAnimationResult = {
          success: false,
          partial: true,
          partial_success: true,
          message: distribResponse.message || "Оплата за газ успішна. Оплату за розподілення не підтверджено.",
          details: [
            {
              label: "Газопостачання",
              value: "Оплачено",
              success: true,
            },
            {
              label: "Розподілення газу",
              value: "Не підтверджено",
              success: false,
            },
          ],
        };
        return;
      }

      if (gasSuccess && distribFailed) {
        const distribError = distribResponse?.error || distribResponse?.message || "";
        this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
        this.$store.commit("Payment/SET_DISTRIBUTION_SAVED_CARD_RESPONSE", null);
        this.paymentAnimationResult = {
          success: false,
          message: "Газ оплачено успішно, але платіж розподілу відхилено" + (distribError ? ": " + distribError : ""),
        };
        return;
      }

      this.showPaymentAnimation = false;
    },

    async handlePaymentResponse() {
      const paymentStep = localStorage.getItem("combinedPaymentStep");
      const isFirstStep = paymentStep === "first";

      let activePaymentUrl = null;
      let activePaymentResponse = null;

      if (this.paymentType === "distribution") {
        const distributionModule = this.currentDistributionModule || this.getDistributionModule();
        activePaymentUrl = this.$store.state[distributionModule].paymentUrl;
        activePaymentResponse = this.distributionPaymentResponse;
      } else {
        activePaymentUrl = this.$store.state.Payment.paymentUrl;
        activePaymentResponse = this.paymentResponse;
      }

      if (activePaymentResponse && (activePaymentResponse.status === "Need3Ds" || activePaymentResponse.actionType === "UrlRedirect")) {
        if (isFirstStep) localStorage.setItem("combinedPaymentStep", "second");
        let redirectUrl = activePaymentUrl;
        if (activePaymentResponse.action) redirectUrl = activePaymentResponse.action;
        else if (activePaymentResponse.alternativeRedirectUrl) redirectUrl = activePaymentResponse.alternativeRedirectUrl;

        if (redirectUrl) {
          if (this.getPlatform !== "ios") this.sendNativeEvent("url", redirectUrl);
        } else {
          await this.safeNavigate("CancelPayment");
        }
      } else {
        if (this.getPlatform !== "ios" && activePaymentUrl) {
          this.sendNativeEvent("url", activePaymentUrl);
        }

        if (activePaymentResponse) {
          if (Object.keys(activePaymentResponse).indexOf("mode") >= 0) {
            if (activePaymentResponse.mode === 1 || activePaymentResponse.success === true) {
              this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
              this.paymentAnimationResult = { success: true, message: activePaymentResponse.message || "Платіж успішно оброблено" };
            } else if (activePaymentResponse.mode === 0 || activePaymentResponse.success === false) {
              this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
              this.paymentAnimationResult = { success: false, message: activePaymentResponse.error || activePaymentResponse.message || "Платіж відхилено банком" };
            }
          } else if (activePaymentResponse.status) {
            if (activePaymentResponse.status === "success" || activePaymentResponse.success === true) {
              this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
              this.paymentAnimationResult = { success: true, message: activePaymentResponse.message || "Платіж успішно оброблено" };
            } else if (activePaymentResponse.status === "error" || activePaymentResponse.success === false) {
              this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
              this.paymentAnimationResult = { success: false, message: activePaymentResponse.error || activePaymentResponse.message || "Платіж відхилено банком" };
            }
          }
        }
      }
    },
  },
};
</script>

<style scoped>
.iframe-container {
  overflow: auto;
  -webkit-overflow-scrolling: touch !important;
  height: calc(100vh - 120px);
}
.iframe-container iframe {
  height: calc(100vh - 120px);
  width: 1px;
  min-width: 100%;
  *width: 100%;
}
.payment-page {
  width: 100%;
  flex-grow: 1;
  border: none !important;
}

/* === UNIFIED PAYMENT === */
.unified-payment {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 16px;
  box-sizing: border-box;
}

/* --- Payment Summary --- */
.payment-summary {
  width: 100%;
  max-width: 480px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: clamp(12px, 3.5vw, 16px);
  margin-bottom: 20px;
  box-sizing: border-box;
}

/* Debt row */
.summary-debt {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.debt-label {
  color: rgba(255, 255, 255, 0.5);
  font-size: clamp(12px, 3vw, 14px);
  font-weight: 500;
}

.debt-value {
  color: #f87171;
  font-size: clamp(15px, 4vw, 18px);
  font-weight: 700;
}

/* Amount to pay row */
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: clamp(13px, 3.5vw, 15px);
  font-weight: 500;
}

.summary-value {
  color: #4fc08d;
  font-size: clamp(18px, 5vw, 22px);
  font-weight: 700;
}

/* Services breakdown */
.summary-services {
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.summary-service {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
}

.summary-service .service-name {
  color: rgba(255, 255, 255, 0.5);
  font-size: clamp(12px, 3vw, 14px);
}

.summary-service .service-amount {
  color: rgba(255, 255, 255, 0.8);
  font-size: clamp(13px, 3.2vw, 15px);
  font-weight: 600;
}

/* Account number */
.summary-account {
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.summary-account span {
  color: rgba(255, 255, 255, 0.4);
  font-size: clamp(11px, 2.8vw, 13px);
}

/* --- Cards --- */
.cards-title {
  color: rgba(255, 255, 255, 0.55);
  font-size: clamp(11px, 3vw, 13px);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 12px;
  text-align: center;
}

.cards-list {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.card-item {
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
}

.card-item:active {
  transform: scale(0.98);
  background: rgba(68, 119, 246, 0.12);
  border-color: rgba(68, 119, 246, 0.35);
}

.card-logo {
  width: 44px;
  height: 30px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-logo-img {
  height: 18px;
  width: auto;
}

.card-pan {
  flex: 1;
  min-width: 0;
  color: #ffffff;
  font-size: clamp(14px, 3.8vw, 16px);
  font-weight: 500;
  letter-spacing: 0.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-pay-label {
  color: #4477f6;
  font-size: clamp(14px, 3.8vw, 16px);
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

/* --- Separator --- */
.separator-text {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-size: clamp(13px, 3.5vw, 15px);
  text-align: center;
  margin: 12px 0 18px;
  font-weight: 500;
  width: 100%;
  max-width: 480px;
}

.separator-text::before,
.separator-text::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.12);
}

/* --- Card Form --- */
.card-form-container {
  width: 100%;
  max-width: 480px;
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: clamp(13px, 3.5vw, 15px);
  text-align: left;
  color: #fff;
}

.form-row {
  display: flex;
  gap: 12px;
}

.half {
  flex: 1;
  min-width: 0;
}

.card-error {
  color: #fff;
  font-size: clamp(12px, 3vw, 14px);
  text-align: center;
  margin-bottom: 12px;
  padding: 12px;
  background-color: rgba(226, 55, 68, 0.2);
  border: 1px solid #e23744;
  border-radius: 8px;
}

.success-message {
  color: #fff;
  font-size: clamp(12px, 3vw, 14px);
  text-align: center;
  margin-bottom: 12px;
  padding: 12px;
  background-color: rgba(56, 161, 105, 0.2);
  border: 1px solid #38a169;
  border-radius: 8px;
}

.terms-checkbox {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 14px 0 0;
  padding: 12px;
  border: 1px solid rgba(94, 104, 135, 0.28);
  border-radius: 14px;
  background: #101520;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
}

.terms-label {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 0;
  color: #fff;
  cursor: pointer;
  min-width: 0;
}

.terms-input {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  margin: 2px 0 0;
  accent-color: #4277f5;
  cursor: pointer;
}

.terms-text {
  min-width: 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.42;
  text-align: left;
  overflow-wrap: anywhere;
}

.terms-link {
  color: #6f8dff;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* --- Pay Button --- */
.pay-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100% !important;
  max-width: none !important;
  height: auto !important;
  padding: 1rem;
  margin-top: 16px;
  background: linear-gradient(135deg, #2653c7, #4477f6) !important;
  border: none !important;
  border-radius: 14px !important;
  color: #ffffff !important;
  font-size: clamp(14px, 3.8vw, 1rem);
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(68, 119, 246, 0.35) !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.pay-btn:active:not(:disabled) {
  transform: scale(0.97);
  box-shadow: 0 3px 10px rgba(68, 119, 246, 0.25) !important;
}

.pay-btn:disabled {
  pointer-events: none;
  opacity: 0.5;
}

/* --- Security Badge --- */
.security-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
  padding-bottom: 16px;
  font-size: clamp(11px, 2.8vw, 13px);
  color: rgba(255, 255, 255, 0.4);
}

.badge-visa {
  color: #1a1f71;
  font-weight: 700;
  font-style: italic;
  background: rgba(255,255,255,0.85);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
}

.badge-mc {
  color: #eb001b;
  font-weight: 700;
  background: rgba(255,255,255,0.85);
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
}

/* --- Input overrides for dark theme --- */
.unified-payment ::v-deep .custom-input {
  background-color: #060b11 !important;
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.2);
  font-size: clamp(14px, 3.8vw, 1rem);
}

.unified-payment ::v-deep .custom-input::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

.unified-payment ::v-deep .custom-input:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.3);
}

/* --- Responsive --- */
@media (max-width: 320px) {
  .unified-payment {
    padding: 12px 8px;
  }
  .payment-summary {
    padding: 10px;
    border-radius: 10px;
  }
  .summary-value {
    font-size: 16px;
  }
  .card-item {
    padding: 10px;
    gap: 8px;
    border-radius: 10px;
  }
  .card-logo {
    width: 34px;
    height: 24px;
  }
  .card-logo-img {
    height: 14px;
  }
  .form-row {
    gap: 6px;
  }
  .pay-btn {
    padding: 12px !important;
    border-radius: 10px !important;
  }
  .card-form-container {
    margin-top: 8px;
  }
}

@media (min-width: 321px) and (max-width: 400px) {
  .unified-payment {
    padding: 14px 10px;
  }
  .payment-summary {
    padding: 12px;
    border-radius: 12px;
  }
  .card-item {
    padding: 12px;
    gap: 10px;
  }
  .card-logo {
    width: 38px;
    height: 26px;
  }
  .card-logo-img {
    height: 15px;
  }
  .form-row {
    gap: 8px;
  }
  .pay-btn {
    padding: 14px !important;
  }
}

@media (min-width: 401px) and (max-width: 480px) {
  .unified-payment {
    padding: 18px 14px;
  }
}

@media (min-width: 768px) {
  .unified-payment {
    padding: 30px 20px;
  }
  .payment-summary {
    padding: 20px;
  }
}
</style>
